
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { usePermissions } from './PermissionsContext'; // Import usePermissions
import PermissionDeniedModal from './components/PermissionDeniedModal'; // Import the modal

function DeleteScheduled() {
  const [scheduledData, setScheduledData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lang, setLang] = useState('en');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [userToken, setUserToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  
  // New state for the permission denied modal
  const [isPermissionDeniedModalOpen, setPermissionDeniedModalOpen] = useState(false);

  const { permissions, isAdmin, loadingPermissions } = usePermissions();

  const translations = useMemo(() => ({
    ja: {
      mainTitle: "予約済み請求書",
      loading: "データを読み込み中...",
      loadMore: "次の50件を読み込む",
      loadingMore: "読み込み中...",
      noMoreData: "これ以上データはありません。",
      noData: "予約データはありません。",
      thCustomer: "顧客ID",
      thIssueAt: "発行日時",
      thAction: "アクション",
      deleteButton: "削除",
      deleteSuccess: "削除完了",
      deleteFail: "削除失敗",
      reloadButton: "再読み込み",
      deleting: "削除中...",
      deleteAllButton: "全予約削除",
      deleteAllConfirm: "本当にすべての予約済み請求書を削除しますか？",
      deleteAllFailed: "全件削除に失敗しました。",
      authLoading: "認証情報を読み込んでいます...",
      authRequired: "設定にアクセスするにはログインしてください。",
    },
    en: {
      mainTitle: "Scheduled Invoices",
      loading: "Loading data...",
      loadMore: "Load Next 50",
      loadingMore: "Loading...",
      noMoreData: "No more data.",
      noData: "No scheduled data found.",
      thCustomer: "Customer ID",
      thIssueAt: "Issue At",
      thAction: "Action",
      deleteButton: "Delete",
      deleteSuccess: "Deleted",
      deleteFail: "Failed",
      reloadButton: "Reload",
      deleting: "Deleting...",
      deleteAllButton: "Delete All",
      deleteAllConfirm: "Are you sure you want to delete all scheduled invoices?",
      deleteAllFailed: "Failed to delete all invoices.",
      authLoading: "Loading authentication...",
      authRequired: "Please log in to access settings.",
    }
  }), []);

  const t = translations[lang];

  useEffect(() => {
    const userLang = navigator.language.startsWith('ja') ? 'ja' : 'en';
    setLang(userLang);
    document.title = "BillWave";
  }, [translations]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      throw new Error('No user token available. Please log in.');
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    if (newOptions.body && typeof newOptions.body === 'object') {
      newOptions.headers['Content-Type'] = 'application/json';
      newOptions.body = JSON.stringify(newOptions.body);
    }
    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let error;
      try {
        const errorJson = JSON.parse(errorText);
        error = new Error(errorJson.error || `Request failed with status ${response.status}`);
      } catch (e) {
        error = new Error(errorText || `Request failed with status ${response.status}`);
      }
      throw error;
    }
    return response;
  }, [userToken]);

  const fetchData = useCallback(async (cursor) => {
    if (!authInitialized || !userToken) return;

    if (cursor === null) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const url = cursor ? `/v1/scheduled?cursor=${cursor}` : '/v1/scheduled';
      const response = await fetchWithAuth(url);
      const data = await response.json();

      const newInvoices = data.invoices.map(item => ({ ...item, status: 'default' }));

      setScheduledData(prev => cursor ? [...prev, ...newInvoices] : newInvoices);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);

    } catch (error) {
      console.error('Fetch error:', error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [authInitialized, userToken, fetchWithAuth]);

  useEffect(() => {
    if (authInitialized) {
        fetchData(null); // Initial fetch
    }
  }, [authInitialized, fetchData]);

  const handleReload = () => {
    setScheduledData([]);
    setNextCursor(null);
    setHasMore(true);
    fetchData(null);
  };

  const handleLoadMore = () => {
    if (nextCursor && hasMore) {
      fetchData(nextCursor);
    }
  };

  const deleteData = async (key) => {
    if (!isAdmin && !permissions.invoice?.canDeleteScheduled) {
        setPermissionDeniedModalOpen(true);
        return;
    }

    setScheduledData(prevData => prevData.map(item => item.key === key ? { ...item, status: 'deleting' } : item));

    try {
      await fetchWithAuth('/v1/delete-invoice', {
        method: 'POST',
        body: { key },
      });
      setScheduledData(prevData => prevData.map(item => item.key === key ? { ...item, status: 'deleted' } : item));
    } catch (error) {
      console.error('Deletion error:', error.message);
      setScheduledData(prevData => prevData.map(item => item.key === key ? { ...item, status: 'failed' } : item));
    }
  };

  const handleDeleteAll = async () => {
    if (!isAdmin && !permissions.invoice?.canDeleteScheduled) {
        setPermissionDeniedModalOpen(true);
        return;
    }
    
    if (window.confirm(t.deleteAllConfirm)) {
      setLoading(true);
      try {
        await fetchWithAuth('/v1/delete-all-scheduled', {
          method: 'DELETE',
        });
        handleReload();
      } catch (error) {
        console.error('Delete all error:', error.message);
        alert(t.deleteAllFailed);
        setLoading(false);
      }
    }
  };

  if (!authInitialized || loadingPermissions) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-500">{t.authLoading}</p>
      </div>
    );
  }

  if (!userToken) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">{t.authRequired}</h1>
          </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <PermissionDeniedModal isOpen={isPermissionDeniedModalOpen} onClose={() => setPermissionDeniedModalOpen(false)} />
      <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900">{t.mainTitle}</h1>
        <div className="flex space-x-2">
          <button onClick={handleDeleteAll} className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors shadow-md" disabled={loading || scheduledData.length === 0}>
            {t.deleteAllButton}
          </button>
          <button onClick={handleReload} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md" disabled={loading || loadingMore}>
            {loading || loadingMore ? t.loading : t.reloadButton}
          </button>
        </div>
      </div>
      <div>
        {loading ? (
          <p className="text-center text-lg text-gray-500">{t.loading}</p>
        ) : scheduledData.length === 0 ? (
          <p className="text-center text-lg text-gray-500 mt-4">{t.noData}</p>
        ) : (
          <>
            <table className="w-full border-collapse mt-4">
              <thead>
                <tr>
                  <th className="border p-2 text-left bg-gray-100">{t.thCustomer}</th>
                  <th className="border p-2 text-left bg-gray-100">{t.thIssueAt}</th>
                  <th className="border p-2 text-left bg-gray-100">{t.thAction}</th>
                </tr>
              </thead>
              <tbody>
                {scheduledData.map((item) => (
                  <tr key={item.key}>
                    <td className="border p-2">{item.customer}</td>
                    <td className="border p-2">{item.issue_at}</td>
                    <td className="border p-2">
                      {item.status === 'default' &&
                        <button onClick={() => deleteData(item.key)} className="delete-btn bg-red-600 text-white py-1 px-3 rounded hover:bg-red-700 transition-colors">
                          {t.deleteButton}
                        </button>
                      }
                      {item.status === 'deleting' &&
                        <span className="text-gray-500 font-bold">{t.deleting}</span>
                      }
                      {item.status === 'deleted' &&
                        <span className="text-green-600 font-bold">{t.deleteSuccess}</span>
                      }
                      {item.status === 'failed' &&
                        <span className="text-red-500 font-bold">{t.deleteFail}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-6">
                {hasMore && (
                    <button onClick={handleLoadMore} disabled={loadingMore} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-blue-400 disabled:cursor-not-allowed">
                        {loadingMore ? t.loadingMore : t.loadMore}
                    </button>
                )}
                {!hasMore && scheduledData.length > 0 && (
                    <p className="text-center text-lg text-gray-500">{t.noMoreData}</p>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DeleteScheduled;
